import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from the back/forward cache.
   * On a bfcache restore the DOM (including the cart bubble) is the stale snapshot from
   * before the navigation, so a product removed before going to checkout would still show
   * in the count. The sessionStorage fast-path in ensureCartBubbleIsCorrect only works
   * within 10s of the last write — useless after a long checkout — so on `persisted` we
   * fetch a fresh cart and recompute the fee-excluded count.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.refreshBubbleFromServer();
    } else {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * The cart bubble must show the MERCHANDISE count, never the hidden `_shipping_fee`
   * line(s). `cart.totalQuantity` (and `detail.itemCount`) are fee-INCLUSIVE (Shopify
   * counts every line), so subtract the fee line quantity carried in the raw `detail.items`
   * (which still has `properties._shipping_fee`). Without this the bubble races the fee
   * engine's updateCartBadge correction and can stick at the fee-inclusive count on any
   * path where the fee engine doesn't re-sync. See [[cart-drawer-audit-findings]].
   * @param {{ properties?: Record<string, string>, quantity?: number }[]} [items]
   * @returns {number}
   */
  #feeQtyFromItems(items) {
    if (!Array.isArray(items)) return 0;
    return items.reduce(
      (sum, item) => sum + (item?.properties?._shipping_fee === 'true' ? item.quantity || 0 : 0),
      0
    );
  }

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const raw = cart?.totalQuantity ?? detail?.itemCount ?? 0;
        const feeQty = this.#feeQtyFromItems(detail?.items);
        this.renderCartBubble(Math.max(0, raw - feeQty));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Fetch /cart.js and render the bubble from the live, fee-excluded merchandise count.
   * Used on bfcache restore (onPageShow persisted) where the DOM snapshot is stale.
   */
  refreshBubbleFromServer = async () => {
    try {
      const res = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const cart = await res.json();
      const feeQty = this.#feeQtyFromItems(cart.items);
      this.renderCartBubble(Math.max(0, (cart.item_count || 0) - feeQty), false);
    } catch (_) {
      // Network/abort — leave the bubble as-is; the next cart event will correct it.
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
